import { cn } from '@/lib/utils';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { InputMasked } from '@chiroup/components/form/InputMasked';
import { Phone } from '@chiroup/components/form/Phone';
import {
  ChiroUpAppointmentCommon,
  ChiroUpDayJsCommon,
} from '@chiroup/core/constants/stringConstants';
import { ConsentMethod } from '@chiroup/core/enums/ConsentMethod.enum';
import { SmsConsentStatus } from '@chiroup/core/enums/SmsConsentStatus.enum';
import { formatPhone } from '@chiroup/core/functions/format';
import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { portalSession } from '../services/portalSession';
import { ScheduleForm } from './Container';
import DejavooCardEntry, { DejavooCardEntryRef } from './DejavooCardEntry';
import EmailOrPhoneLogin, { VerifiedGuestContact } from './EmailOrPhoneLogin';
import SchedulePatients from './SchedulePatients';

// Custom auth replaces Cognito. Local stand-ins matching the shape
// the component used.
type AuthUser = { userId: string; username: string };

const getCurrentUser = async (): Promise<AuthUser> => {
  const patient = portalSession.getPatient();

  if (!portalSession.isLikelyValid() || !patient) {
    throw new Error('Not authenticated');
  }

  return { userId: patient.patientId, username: patient.patientId };
};

type Props = {
  values: ScheduleForm;
  setValues: React.Dispatch<React.SetStateAction<ScheduleForm>>;
  onChange: (key: string) => (value: any) => void;
  treatmentName: string;
  providerName: string;
  providerImage?: string;
  appointmentDate: string;
  appointmentTime: string;
  makeChanges: () => void;
  setUser: (user: AuthUser) => void;
  path: string;
  getMe: (force?: boolean) => Promise<void>;
  me: {
    patients: any[];
  } | null;
  confirm: () => void;
  isSaving: boolean;
  error: string | null;
  locationData: HealthComLocation;
  isCreditCardRequired: boolean;
  epayPublicKey: string | null;
  processor: 'usaepay' | 'dejavoo' | null;
  dejavooEcommToken: string | null;
  dejavooCardEntryRef: React.MutableRefObject<DejavooCardEntryRef | null>;
  hasPhoneConsent: boolean;
  /**
   * `true` when rendering inside the public booking shell
   * (`/clinic-schedule/<path>`). The shell has no BottomNav, so the
   * sticky action bar pins to the actual viewport bottom instead of
   * leaving a 76px clearance for nav. Also disables the desktop
   * "inline at end of form" fallback — public mode keeps the action
   * bar visible at all widths since the form is the only thing on
   * the page.
   */
  isPublic?: boolean;
  /**
   * Forwarded to the embedded `<Login>` so the public booking flow can
   * stay on this confirm screen after auth (and let the user click
   * "Book" to actually create the appointment) instead of bouncing to
   * a path-based home route.
   */
  loginOnAuthSuccess?: () => void;
  /**
   * Verified-but-no-Patient marker, owned by the Schedule parent.
   * When the OTP login below resolves with no matching patient rows we
   * push the verified contact + live challengeId up here so the
   * parent's `confirm()` routes the booking through
   * /appointment-guest instead of the authed /appointment.
   */
  verifiedGuest?: VerifiedGuestContact | null;
  onVerifiedGuestChange?: (next: VerifiedGuestContact | null) => void;
};

const ScheduleConfirm = ({
  values,
  setValues,
  onChange,
  treatmentName,
  providerName,
  providerImage,
  appointmentDate,
  appointmentTime,
  makeChanges,
  setUser,
  path,
  getMe,
  me,
  confirm,
  isSaving,
  error,
  locationData,
  isCreditCardRequired,
  epayPublicKey,
  processor,
  dejavooEcommToken,
  dejavooCardEntryRef,
  hasPhoneConsent,
  isPublic = false,
  loginOnAuthSuccess,
  verifiedGuest = null,
  onVerifiedGuestChange,
}: Props) => {
  const [otherUserOpen, setOtherUserOpen] = useState(false);
  const [CCError, setCCError] = useState<string | null>(null);
  const lastPhoneRef = useRef<string | null>(values.phone || null);

  useEffect(() => {
    const current = values.phone || null;

    if (current !== lastPhoneRef.current) {
      lastPhoneRef.current = current;
      setValues?.((prev: any) => ({
        ...prev,
        smsConsent: null,
        smsConsentMethod: null,
      }));
    }
  }, [setValues, values.phone]);

  // Mirror the verified OTP contact onto the form values so it ships
  // with the booking submit and shows in the locked field below. We
  // only set the matching channel — leaving the other free for the
  // user to fill in if they want both on file.
  useEffect(() => {
    if (!verifiedGuest) return;

    setValues?.((prev: any) => {
      if (verifiedGuest.contactType === 'email' && prev.email !== verifiedGuest.contact) {
        return { ...prev, email: verifiedGuest.contact };
      }

      if (verifiedGuest.contactType === 'phone' && prev.phone !== verifiedGuest.contact) {
        return { ...prev, phone: verifiedGuest.contact };
      }

      return prev;
    });
  }, [setValues, verifiedGuest]);

  const okayToConfirm = () => {
    if (values.phone && !hasPhoneConsent && values.smsConsent !== SmsConsentStatus.OPTED_IN) {
      return false;
    }

    if (isCreditCardRequired) {
      if (!values.creditCardConsent) {
        return false;
      }
    }

    if (values.patient) {
      return true;
    }

    if (me) {
      return values.fname && values.lname;
    }

    // Verified-guest path AND legacy fully-unauthed path share the
    // same fname/lname/DOB requirement: a new pending patient needs
    // enough identity to be recognizable in the staff queue. DOB is
    // validated by length because the masked input emits the full
    // `MM/DD/YYYY` once filled.
    return values.fname && values.lname && (values.dateOfBirth || '').length === 10;
  };

  const toggleOtherUserOpen = () => {
    setOtherUserOpen(prev => !prev);
  };

  const scheduleForSomeoneElse = () => {
    setValues?.((p: any) => ({
      ...p,
      patient: null,
      fname: null,
      lname: null,
      email: null,
      phone: null,
      dateOfBirth: null,
    }));
    setOtherUserOpen(true);
  };

  const handlePaymentCardError = useCallback(
    (err: any) => {
      err = JSON.parse(err);

      if (err.code === '0') {
        setCCError(null);
      } else if (err.code) {
        // Payment card validation errors (missing card number, invalid expiry, etc.)
        // are expected user input issues — display them in the UI, don't send to Sentry.
        setCCError(err.message);
      }
    },
    [setCCError]
  );

  useEffect(() => {
    let paymentCard: any = null;

    // `me` / `verifiedGuest` is part of the gate because the
    // `#paymentCardContainer` element only renders once the patient is
    // either authenticated or has OTP-verified their contact (see the
    // `(me || verifiedGuest) && isCreditCardRequired` block below).
    // Without those in the dep list this effect would fire on the
    // unauthenticated render, find no container to attach to, and
    // never re-run after login / verification.
    if (
      (window as any).usaepay &&
      (me || verifiedGuest) &&
      isCreditCardRequired &&
      processor !== 'dejavoo'
    ) {
      const client = new (window as any).usaepay.Client(epayPublicKey);
      paymentCard = client.createPaymentCardEntry();

      const paymentCardConfig = {
        styles: `
        #payjs-container {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          padding: 0 !important;
          gap: 6px;
        }
        #payjs-input-icon {
          display: none;
        }
        #payjs-cnum-wrapper {
          grid-column: 1 / 12;
          width: 100%;
          padding: 0 !important;
        }
        #payjs-exp-wrapper {
          grid-column: 1 / 6;
          width: 100%;
          padding: 0 !important;
        }
        #payjs-cvv-wrapper {
          grid-column: 7 / 12;
          width: 100%;
          padding: 0 !important;
        }
        .payjs-container input {
          border: 1px solid !important;
          border-color: #d2d5db !important;
          border-radius: 12px !important;
          height: 36px;
          padding-left: 10px;
        }
        @media screen and (max-width: 400px) {
            .payjs-base {
                font-size: 12px;
            }
        }
          `,
      };

      paymentCard.generateHTML(paymentCardConfig);
      paymentCard.addHTML('paymentCardContainer');
      paymentCard.addEventListener('error', handlePaymentCardError);
    }
  }, [handlePaymentCardError, isCreditCardRequired, epayPublicKey, processor, me, verifiedGuest]);

  return (
    <div className="flex flex-col gap-4 relative z-10" id="confirm">
      {isSaving && <div className="absolute left-0 right-0 grid place-items-center h-full z-20" />}

      {/* Page heading — drops the wizard's "Book a visit" in favor of
          a clearer call-out that this screen still requires an action.
          We saw patients leave at this step thinking the booking was
          already done, so the copy explicitly names the final tap.
          When the patient hasn't authenticated yet, swap to login-focused
          copy so the call-to-action matches the only thing they can do
          on the screen. */}
      <div>
        {!me && !verifiedGuest ? (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight text-gray-900 dark:text-gray-100">
              Let&apos;s get you booked
            </h1>
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300">
              Enter your mobile number or email below.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight text-gray-900 dark:text-gray-100">
              You’re almost booked
            </h1>
            <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300">
              Selecting “Book Appointment” below is your final step.
            </p>
          </>
        )}
      </div>

      {/* Sign-in / create-account card. Rendered above the appointment
          summary while unauthenticated so the only available action is
          the first thing the patient sees. Once authenticated, the
          equivalent patient-selection UI renders below the summary. */}
      {!me && !verifiedGuest && (
        <div className="portal-card p-5">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Sign in or create your account
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              We&apos;ll send a verification code so you can confirm this booking.
            </p>
            <EmailOrPhoneLogin
              onAuthenticated={async () => {
                // Mirror the legacy <Login>'s post-OTP work: pull the
                // fresh Cognito session into Schedule's parent state,
                // and prefetch /me/{path} so this confirm screen can
                // render the patient picker / new-patient form on
                // re-render.
                setUser(await getCurrentUser());
                // `force: true` — a pre-auth /me 401 may have already
                // tripped getMe's once-per-path gate. Without forcing, this
                // refetch no-ops, `me` stays null, and this screen sits on
                // the OTP prompt until a manual refresh.
                await getMe(true);
                loginOnAuthSuccess?.();
              }}
              onVerifiedWithoutMatch={({ contact, contactType, challengeId }) => {
                // No Patient row matched the OTP-verified contact. Push
                // the verified state up to the Schedule parent so the
                // booking call routes through /appointment-guest and
                // creates a pending Patient inline. The form below
                // collects the rest of the identity (fname/lname/DOB).
                onVerifiedGuestChange?.({ contact, contactType, challengeId });
              }}
              submitLabel="Continue"
              verifyLabel="Confirm"
              clinicId={locationData?.clinicId}
            />
          </div>
        </div>
      )}

      {/* Appointment summary card */}
      <div className="portal-card p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Appointment Summary
          </h3>
          <button
            type="button"
            onClick={makeChanges}
            disabled={isSaving && !error}
            className="text-sm font-semibold text-[var(--primary-color)] hover:underline disabled:opacity-50"
          >
            Change
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            {providerImage ? (
              <img
                src={providerImage}
                alt={providerName}
                className="h-10 w-10 rounded-xl object-cover"
              />
            ) : (
              <UserCircleIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
            )}
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {providerName}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">{treatmentName}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 dark:text-gray-600">
            <CalendarDaysIcon className="h-5 w-5 text-gray-300 dark:text-gray-600" />
            <span>{ChiroUpDayJsCommon.cleanupDatetime(appointmentDate)}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300 dark:text-gray-600">
            <ClockIcon className="h-5 w-5 text-gray-300 dark:text-gray-600" />
            <span>{ChiroUpDayJsCommon.cleanupDatetime(appointmentTime)}</span>
          </div>
        </div>
      </div>

      {/* Patient selection (authenticated only — the login form for
          unauthenticated patients renders above the summary). */}
      {me && (
        <div className="portal-card p-5">
          {me?.patients?.length && otherUserOpen ? (
            <div>
              <div
                onClick={toggleOtherUserOpen}
                className="cursor-pointer text-[var(--primary-color)] hover:underline flex flex-row gap-2 items-center text-sm font-medium"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to list</span>
              </div>
              <div className="mt-4 flex flex-col gap-3">
                <Input
                  label="First name *"
                  type="text"
                  name="fname"
                  value={values.fname}
                  onChange={onChange('fname')}
                  className="col-span-1"
                />
                <Input
                  label="Last name *"
                  type="text"
                  name="lname"
                  value={values.lname}
                  onChange={onChange('lname')}
                  className="col-span-1"
                />
                <InputMasked
                  label="Date of birth"
                  name="dateOfBirth"
                  value={values.dateOfBirth}
                  onChange={e => {
                    setValues?.((p: any) => ({
                      ...p,
                      dateOfBirth: e,
                    }));
                  }}
                  patternFormat="##/##/####"
                  placeholder="MM/DD/YYYY"
                />
              </div>
            </div>
          ) : me?.patients?.length ? (
            (() => {
              // If a patient is already picked (auto-selected from the
              // portal context, or picked earlier in the flow) skip the
              // list and show a compact confirmation. Users can still
              // switch patients or schedule for someone else via the
              // links below.
              const selectedMePatient = values.patient
                ? me.patients.find((p: any) => p.ID === values.patient)
                : null;

              if (selectedMePatient) {
                const canSwitch = me.patients.length > 1;

                return (
                  <div className="flex flex-col gap-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Who is this appointment for?
                    </h3>
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-white font-bold text-sm"
                        style={{ backgroundColor: 'var(--primary-color)' }}
                      >
                        {selectedMePatient.fname?.charAt(0)?.toUpperCase()}
                        {selectedMePatient.lname?.charAt(0)?.toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {selectedMePatient.fname} {selectedMePatient.lname}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      {canSwitch && (
                        <button
                          type="button"
                          onClick={() =>
                            setValues?.((p: any) => ({
                              ...p,
                              patient: null,
                            }))
                          }
                          className="text-[var(--primary-color)] hover:underline font-medium"
                        >
                          Switch patient
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={scheduleForSomeoneElse}
                        className="text-[var(--primary-color)] hover:underline font-medium"
                      >
                        Schedule for new patient
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div className="flex flex-col gap-4">
                  <SchedulePatients
                    value={values.patient}
                    onChange={onChange('patient')}
                    patients={me?.patients || []}
                  />
                  <div className="w-full text-center text-sm">
                    <div
                      onClick={scheduleForSomeoneElse}
                      className="cursor-pointer text-[var(--primary-color)] hover:underline font-medium"
                    >
                      Need to schedule an appointment for someone else?
                    </div>
                  </div>
                </div>
              );
            })()
          ) : me ? (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="First name"
                type="text"
                name="fname"
                value={values.fname}
                onChange={onChange('fname')}
                className="col-span-1"
              />
              <Input
                label="Last name"
                type="text"
                name="lname"
                value={values.lname}
                onChange={onChange('lname')}
                className="col-span-1"
              />
              <Input
                label="Email"
                type="email"
                name="email"
                value={values.email}
                onChange={onChange('email')}
                className="col-span-2"
              />
              <Phone
                label="Phone"
                name="phone"
                value={values.phone}
                onChange={onChange('phone')}
                className="text-gray-900 dark:text-gray-100 font-medium col-span-2"
                international
              />
              {values.phone && !hasPhoneConsent ? (
                <div className="col-span-2">
                  <div className="mt-1 rounded-xl border border-blue-200 dark:border-blue-500/20 bg-blue-50 dark:bg-blue-500/10 p-3">
                    <Checkbox
                      value={values.smsConsent === SmsConsentStatus.OPTED_IN}
                      onChange={checked =>
                        setValues?.((prev: any) => ({
                          ...prev,
                          smsConsent: checked ? SmsConsentStatus.OPTED_IN : null,
                          smsConsentMethod: checked ? ConsentMethod.WEB_FORM : null,
                        }))
                      }
                      label="I agree to receive appointment-related text messages."
                      className="text-sm font-normal"
                    />
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 dark:text-gray-600">
                      Message and data rates may apply. Reply STOP to opt out. Reply HELP for help.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {/* Verified-guest new-patient form. The OTP step proved ownership
          of the contact below; we just need name + DOB to mint the
          pending Patient row. The verified contact is shown locked so
          it can't be accidentally edited away from what was actually
          verified. */}
      {!me && verifiedGuest && (
        <div className="portal-card p-5">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              A few more details
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              We&apos;ve confirmed your {verifiedGuest.contactType === 'email' ? 'email' : 'phone'}.
              Fill in your name and date of birth so the clinic can prepare for your visit.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="First name *"
                type="text"
                name="fname"
                value={values.fname}
                onChange={onChange('fname')}
                className="col-span-1"
              />
              <Input
                label="Last name *"
                type="text"
                name="lname"
                value={values.lname}
                onChange={onChange('lname')}
                className="col-span-1"
              />
              {verifiedGuest.contactType === 'email' ? (
                <Input
                  label="Email (verified)"
                  type="email"
                  name="email"
                  value={verifiedGuest.contact}
                  onChange={() => {
                    /* locked — verified contact comes from OTP, not the form. */
                  }}
                  disabled
                  className="col-span-2"
                />
              ) : (
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={onChange('email')}
                  className="col-span-2"
                />
              )}
              {verifiedGuest.contactType === 'phone' ? (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone (verified)
                  </label>
                  <div className="mt-1.5 rounded-xl bg-gray-100 dark:bg-gray-900 px-3.5 py-2.5 text-sm text-gray-700 dark:text-gray-200 tabular-nums">
                    {formatPhone(verifiedGuest.contact) || verifiedGuest.contact}
                  </div>
                </div>
              ) : (
                <Phone
                  label="Phone"
                  name="phone"
                  value={values.phone}
                  onChange={onChange('phone')}
                  className="text-gray-900 dark:text-gray-100 font-medium col-span-2"
                  international
                />
              )}
              <InputMasked
                label="Date of birth *"
                name="dateOfBirth"
                value={values.dateOfBirth}
                onChange={e => {
                  setValues?.((p: any) => ({
                    ...p,
                    dateOfBirth: e,
                  }));
                }}
                patternFormat="##/##/####"
                placeholder="MM/DD/YYYY"
              />
              {values.phone && !hasPhoneConsent ? (
                <div className="col-span-2">
                  <div className="mt-1 rounded-xl border border-blue-200 dark:border-blue-500/20 bg-blue-50 dark:bg-blue-500/10 p-3">
                    <Checkbox
                      value={values.smsConsent === SmsConsentStatus.OPTED_IN}
                      onChange={checked =>
                        setValues?.((prev: any) => ({
                          ...prev,
                          smsConsent: checked ? SmsConsentStatus.OPTED_IN : null,
                          smsConsentMethod: checked ? ConsentMethod.WEB_FORM : null,
                        }))
                      }
                      label="I agree to receive appointment-related text messages."
                      className="text-sm font-normal"
                    />
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 dark:text-gray-600">
                      Message and data rates may apply. Reply STOP to opt out. Reply HELP for help.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Credit card & consent — gated on `me` OR `verifiedGuest` so the
          card capture UI doesn't appear until the patient has either
          signed in via OTP (authed portal) or proved ownership of their
          contact via OTP (new-patient public booking). Pre-auth there's
          no patient to attach the card to and asking for payment
          details up-front is confusing; the sign-in card above is the
          only action that should be visible. */}
      {(me || verifiedGuest) && isCreditCardRequired && (
        <div className="portal-card p-5">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Payment information required
          </div>
          {processor === 'dejavoo' && dejavooEcommToken ? (
            <DejavooCardEntry
              ref={dejavooCardEntryRef}
              ecommToken={dejavooEcommToken}
              onTokenReceived={() => {
                /* consumed via ref.tokenize() return value in Schedule.confirm */
              }}
              onError={err => setCCError(err)}
              isSubmitting={isSaving}
              defaultCardholderName={[values.fname, values.lname].filter(Boolean).join(' ') || ''}
              showSaveCard={false}
            />
          ) : processor === 'dejavoo' ? (
            <div className="text-sm text-red-600 dark:text-red-400">
              Unable to load the payment form. Please contact the clinic to complete your booking.
            </div>
          ) : (
            <>
              <div id="paymentCardContainer" className="h-24">
                {/* Payment Card iframe will go here */}
              </div>
              <div id="paymentCardErrorContainer">{/* Display error messages here */}</div>
            </>
          )}
          <Checkbox
            value={values.creditCardConsent}
            onChange={onChange('creditCardConsent')}
            label={`By saving this card, I authorize ${locationData?.name || 'the clinic'} to store my payment information for future use.`}
            className="text-sm font-normal"
            tooltip="By submitting your card, you consent to it being securely stored and authorize charges for services or fees. An authorization transaction for $0.01 may appear for a short time."
          />
        </div>
      )}

      {/*
       * SMS consent
       *
       * Only render the consent card when we actually need to collect
       * consent. If `hasPhoneConsent` is already true, we'd be showing
       * the user a "your consent is on file" note with no action — just
       * visual noise. Hiding here is a patient-portal-specific choice;
       * this component only lives in healthcom-schedule today (grep'd to
       * confirm), so we don't risk suppressing the card in ChiroUp.
       */}
      {values.phone && !hasPhoneConsent && (
        <div className="portal-card p-5">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Text messaging consent
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 break-all">
            {formatPhone(values.phone)}
          </div>
          <div className="mt-3">
            <Checkbox
              value={values.smsConsent === SmsConsentStatus.OPTED_IN}
              onChange={checked =>
                setValues?.((prev: any) => ({
                  ...prev,
                  smsConsent: checked ? SmsConsentStatus.OPTED_IN : null,
                  smsConsentMethod: checked ? ConsentMethod.WEB_FORM : null,
                }))
              }
              label="I agree to receive appointment-related text messages."
              className="text-sm font-normal"
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Message and data rates may apply. Reply STOP to opt out. Reply HELP for help.
            </p>
          </div>
        </div>
      )}

      {/* Errors */}
      {(error || CCError) && (
        <div className="portal-card p-5">
          {error === ChiroUpAppointmentCommon.noSameDay.code ? (
            <p className="text-sm text-red-600">{ChiroUpAppointmentCommon.noSameDay.friendly}</p>
          ) : error === ChiroUpAppointmentCommon.noPast.code ? (
            <p className="text-sm text-red-600">{ChiroUpAppointmentCommon.noPast.friendly}</p>
          ) : error === ChiroUpAppointmentCommon.tooSoon.code ? (
            <p className="text-sm text-red-600">{ChiroUpAppointmentCommon.tooSoon.friendly}</p>
          ) : typeof error === 'string' ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : CCError ? (
            <p className="text-sm text-red-600">{CCError}</p>
          ) : null}
        </div>
      )}

      {/*
       * Action bar.
       * - public flow (`/clinic-schedule/<path>`): pinned to the actual
       *   viewport bottom at every breakpoint. The shell has no
       *   BottomNav so there's nothing to clear, and the form is the
       *   only thing on the page — keeping the buttons in view at all
       *   times is the right behavior.
       * - portal flow at `< lg`: pinned above the BottomNav (`bottom-[4.75rem]`).
       * - portal flow at `lg+`: BottomNav is hidden by the desktop
       *   sidebar layout, so the bar renders inline at the end of the
       *   form instead of floating in empty space.
       */}
      {/* Hide the action bar entirely while the patient hasn't either
          authenticated (existing portal user) OR proved ownership of
          their contact via OTP (new-patient guest path). Pre-auth the
          only forward action is the OTP login above, which submits via
          its own button. */}
      {(me || verifiedGuest) && (
        <>
          <div className={cn(isPublic ? 'pb-24 sm:pb-28' : 'pb-28 lg:pb-0')} aria-hidden="true" />
          <div
            className={cn(
              'fixed inset-x-0 z-30',
              isPublic
                ? 'bottom-0 pb-[max(env(safe-area-inset-bottom),0.5rem)]'
                : 'lg:hidden bottom-[4.75rem]'
            )}
          >
            <div
              className={cn(
                'mx-auto px-4 sm:px-6',
                isPublic ? 'max-w-3xl lg:max-w-5xl' : 'max-w-lg'
              )}
            >
              <ActionBarButtons
                confirm={confirm}
                isSaving={isSaving}
                okayToConfirm={okayToConfirm}
              />
            </div>
          </div>
          {!isPublic && (
            <div className="hidden lg:block">
              <ActionBarButtons
                confirm={confirm}
                isSaving={isSaving}
                okayToConfirm={okayToConfirm}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

type ActionBarButtonsProps = {
  confirm: () => void;
  isSaving: boolean;
  okayToConfirm: () => any;
};

const ActionBarButtons: React.FC<ActionBarButtonsProps> = ({
  confirm,
  isSaving,
  okayToConfirm,
}) => (
  <div className="rounded-2xl bg-white/90 dark:bg-[var(--color-bg-card)] backdrop-blur-md ring-1 ring-gray-950/5 dark:ring-white/10 shadow-[0_-2px_16px_rgba(0,0,0,0.05)] dark:shadow-none p-2">
    <button
      type="button"
      onClick={confirm}
      disabled={isSaving || !okayToConfirm()}
      className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-50 active:opacity-80"
      style={{ backgroundColor: 'var(--primary-color)' }}
    >
      {isSaving ? 'Booking…' : 'Book Appointment'}
    </button>
  </div>
);

export default ScheduleConfirm;
